import React from 'react';
import {Button} from '@mantine/core';
import {Center} from 'src/design/atoms/Center';
import {Stack} from 'src/design/atoms/Stack';
import {IconPlus} from '@tabler/icons-react';
import type {
  NotificationRuleChangeCondition,
  NotificationRuleMetadata,
} from 'src/api/ProactApiClient';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import type {
  ChangeEventConditionData,
  ChangeEventModuleConfig,
} from '../config/changeEventConfig.types';
import {ChangeEventConditionRow} from './ChangeEventConditionRow';

export interface ChangeEventConditionListProps {
  config: ChangeEventModuleConfig;
  metadata: NotificationRuleMetadata;
  onMetadataChange: (metadata: NotificationRuleMetadata) => void;
  disabled: boolean;
}

/**
 * Generic condition list component that manages an array of conditions.
 * Replaces all module-specific EditNotification*ChangeEventRules components.
 */
export const ChangeEventConditionList: React.FC<
  ChangeEventConditionListProps
> = ({config, metadata, onMetadataChange, disabled}) => {
  // Get the conditions array from metadata using the config's field name
  const conditions = (metadata[config.conditionsFieldName] ??
    []) as ChangeEventConditionData[];

  const handleAdd = () => {
    // Find the default value for the default property
    const defaultValueConfig = config.valueConfigs.find(
      vc => vc.property === config.defaultProperty
    );

    const newCondition: NotificationRuleChangeCondition = {
      property: config.defaultProperty,
      operator: config.defaultOperator,
      value: defaultValueConfig?.defaultValue ?? '',
    };

    onMetadataChange({
      ...metadata,
      [config.conditionsFieldName]: [...conditions, newCondition],
    });
  };

  const handleChange = (condition: ChangeEventConditionData, index: number) => {
    const newConditions = [...conditions];
    newConditions[index] = condition;
    onMetadataChange({
      ...metadata,
      [config.conditionsFieldName]: newConditions,
    });
  };

  const handleDelete = (
    _condition: ChangeEventConditionData,
    index: number
  ) => {
    onMetadataChange({
      ...metadata,
      [config.conditionsFieldName]: [
        ...conditions.slice(0, index),
        ...conditions.slice(index + 1),
      ],
    });
  };

  const addButton = (
    <Button
      data-testid="add-condition-button"
      onClick={handleAdd}
      size="sm"
      leftSection={<IconPlus />}
      disabled={disabled}>
      {conditions.length > 0 ? 'Add Condition' : 'Add First Condition'}
    </Button>
  );

  if (conditions.length === 0) {
    return (
      <NoItemsPanel
        canCreate={!disabled}
        createNode={addButton}
        noItemsText="Conditions determine when this notification triggers. Add conditions to match specific changes in the system."
      />
    );
  }

  return (
    <Stack gap="md">
      {conditions.map((condition, index) => (
        <ChangeEventConditionRow
          key={index}
          config={config}
          data={condition}
          index={index}
          disabled={disabled}
          metadata={metadata}
          onChange={handleChange}
          onDelete={handleDelete}
        />
      ))}
      <Center mt="xl">{addButton}</Center>
    </Stack>
  );
};
